import { APP_EMITTER_EVENTS, AppEmitter, DatetimePicker, FormLabel } from "shared";
import { ITemplateDateInputComponent } from "entities/template";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { IValidationFunc } from "../../../../lib/types/validation";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_date.module.scss";

interface IDocumentFormDateProps {
    component: ITemplateDateInputComponent;
    onChange: (partial: Record<string, any>) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormDate: React.FC<IDocumentFormDateProps> = React.memo(({
                                                                                  component,
                                                                                  label,
                                                                                  tooltip,
                                                                                  readonly,
                                                                                  labelShort,
                                                                                  triggerValidate,
                                                                                  onValidationRegister,
                                                                                  externalUpdateRecord,
                                                                                  onChange,
                                                                              }) => {
    const [value, setValue] = useState<string | null>(null);

    const required = useMemo(() => {
        return component.required !== false;
    }, [component])

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true)
            }, component.name);
            onChangeDate(null);
        }
    }, [])

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, [])

    useEffect(() => {
        if (component.name) {
            onValidationRegister(validateDate, component.name);
        }
    }, [component.name, value, required])

    useEffect(() => {
        if (component.isDefaultDate !== false) {
            const externalDateString = externalUpdateRecord[component.name];
            if (!externalDateString) {
                const defaultDate = dayjs().format("DD.MM.YYYY");
                setTimeout(() => {
                    onChangeDate(defaultDate);
                    setValue(defaultDate);
                }, 200)
            }
        }
    }, [])

    useEffect(() => {
        if (component.name) {
            const dateString = externalUpdateRecord[component.name];
            setValue(dateString);
            onChangeDate(dateString ? dateString : null);
        }
    }, [externalUpdateRecord, component.name])

    const validateDate = (): Promise<boolean> => {
        const valid = !required || !!value;
        return Promise.resolve(valid);
    }

    const handleClearEvent = () => {
        if (!readonly) {
            onChangeDate(null);
        }
    }

    const onChangeDate = (date: string | null) => {
        const key = component.name;
        setValue(date);
        if (key) {
            if (date) {
                const dateSplit = date.split(".");
                onChange({
                    [key]: date,
                    [`${key}_dd`]: dateSplit[0],
                    [`${key}_mm`]: dateSplit[1],
                    [`${key}_yyyy`]: dateSplit[2]
                })
            } else {
                onChange({
                    [key]: undefined,
                    [`${key}_dd`]: undefined,
                    [`${key}_mm`]: undefined,
                    [`${key}_yyyy`]: undefined
                })
            }
        }
    }

    return <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <DatetimePicker
                disabled={readonly}
                validate={required}
                className={styles.input}
                triggerValidate={triggerValidate}
                initialValue={value}
                dateFormat="DD.MM.YYYY"
                onUpdate={onChangeDate}
                withTime={false}
            />
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel>;
});
